import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AlertTriangle, ShieldCheck } from 'lucide-react';
import { api, ApiError } from '@/api/client';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { PublicShell } from '@/components/PublicShell';

export const Route = createFileRoute('/login/2fa')({
  component: LoginTwoFactorPage,
});

const schema = z.object({
  code: z.string().min(6, 'Six-digit code required'),
  rememberMachine: z.boolean().default(false),
});
type Values = z.infer<typeof schema>;

function LoginTwoFactorPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', rememberMachine: false },
  });

  const submit = useMutation({
    mutationFn: (v: Values) => api('/auth/login-with-2fa', { method: 'POST', json: v }),
    onSuccess: () => navigate({ to: '/returns' }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const problem = err.problem as { title?: string } | null;
        setError(problem?.title ?? 'Invalid code');
      } else setError('Could not reach server');
    },
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-xl px-6 py-16">
        <div className="section-ref">Step 2 of 2 — verify identity</div>
        <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight">
          Enter your authenticator code
        </h1>
        <div className="mt-3 gov-rule" aria-hidden />
        <p className="mt-6 flex items-start gap-2 text-[15px] leading-relaxed text-(--color-ink-2)">
          <ShieldCheck className="mt-1 size-4 shrink-0 text-(--color-navy)" />
          Open your authenticator app and enter the six-digit code shown for NightTax.
        </p>

        <Card className="mt-8">
          <CardContent className="p-8">
            <form
              onSubmit={form.handleSubmit((v) => {
                setError(null);
                submit.mutate(v);
              })}
              className="space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="tfa-code" required>
                  Authenticator code
                </Label>
                <Input
                  id="tfa-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="font-mono tracking-[0.4em] text-center text-xl"
                  maxLength={6}
                  aria-invalid={!!form.formState.errors.code || undefined}
                  {...form.register('code')}
                />
                {form.formState.errors.code && (
                  <p className="flex items-center gap-1.5 text-[12px] font-medium text-(--color-maroon)">
                    <AlertTriangle className="size-3.5" />
                    {form.formState.errors.code.message}
                  </p>
                )}
              </div>
              <label className="flex items-center gap-2.5 text-[13px] text-(--color-ink-2)">
                <input
                  type="checkbox"
                  className="size-4 rounded-(--radius-sm) border-[1.5px] border-(--color-rule-strong) accent-(--color-navy)"
                  {...form.register('rememberMachine')}
                />
                Trust this device for 30 days
              </label>
              {error && (
                <p className="flex items-start gap-2.5 rounded-(--radius-sm) border-l-[3px] border-(--color-maroon) bg-(--color-maroon-soft) px-3 py-2.5 text-[13px] font-medium text-(--color-maroon)">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                  {error}
                </p>
              )}
              <Button type="submit" className="w-full" size="lg" disabled={submit.isPending}>
                {submit.isPending ? 'Verifying…' : 'Verify and sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-[14px] text-(--color-ink-2)">
          Lost access to your authenticator?{' '}
          <Link to="/login/recovery-code" className="gov-link font-medium">
            Use a recovery code
          </Link>
        </p>
      </div>
    </PublicShell>
  );
}
